let reconnectTimer = null;
const typingTimers = {};

import { create } from 'zustand';
import api from '../services/api';
import { setWsUp } from '../services/connectivity';
import { playBlaBlaSound, showBrowserNotification, ensureNotificationsEnabled } from '../services/notifications';

const WS_URL = process.env.REACT_APP_WS_URL;

function buildWsUrl() {
  if (WS_URL) {
    if (WS_URL.startsWith('/')) return `wss://${window.location.host}${WS_URL}`;
    return WS_URL;
  }
  const host = window.location.hostname;
  if (host === 'xn--90aialydaagkmm8g.xn--p1ai') return `wss://${host}/ws/chat`;
  return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/chat`;
}

function getCurrentUserId() {
  try {
    return JSON.parse(localStorage.getItem('trandi-auth') || '{}')?.state?.user?.id || null;
  } catch (e) {
    return null;
  }
}

export const useChatStore = create((set, get) => ({
  chats: [],
  activeChat: null,
  messages: {},
  fetchedChats: {},
  fetchingChats: {},
  typingUsers: {},
  unreadCounts: {},
  socket: null,
  onlineUsers: {},
  isConnected: false,
  fetchError: '',

  connectSocket: (token) => {
    const wsUrl = `${buildWsUrl()}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      setWsUp(true);
      set({ isConnected: true });
      get().fetchChats().catch(() => {});
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'new_message': {
            const msg = data.data;
            set((s) => {
              if (!s.typingUsers[msg.chatId]) return {};
              const t = { ...s.typingUsers };
              delete t[msg.chatId];
              return { typingUsers: t };
            });
            const msgs = get().messages;
            const existing = msgs[msg.chatId] || [];
            if (!existing.find((m) => m.id === msg.id)) {
              const chatMsgs = [...existing, msg].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
              set({ messages: { ...msgs, [msg.chatId]: chatMsgs } });
            }

            const scheduled = get().scheduledMessages;
            if (scheduled.some((m) => m.id === msg.id)) {
              set({ scheduledMessages: scheduled.filter((m) => m.id !== msg.id) });
            }

            set((s) => {
              const chat = s.chats.find((c) => c.id === msg.chatId);
              if (!chat) return {};
              const updated = { ...chat, lastMessage: msg };
              return { chats: [updated, ...s.chats.filter((c) => c.id !== msg.chatId)] };
            });

            const currentUserId = getCurrentUserId();
            const isActive = get().activeChat === msg.chatId;
            if (msg.senderId !== currentUserId) {
              if (isActive) {
                set((s) => ({ unreadCounts: { ...s.unreadCounts, [msg.chatId]: 0 } }));
                get().markChatRead(msg.chatId);
              } else {
                const chatMuted = get().chats.find((c) => c.id === msg.chatId)?.muted;
                if (!chatMuted) {
                  playBlaBlaSound();
                  const chats = get().chats;
                  const chat = chats.find((c) => c.id === msg.chatId);
                  const chatName = chat?.name || msg.senderDisplayName || 'Новое сообщение';
                  const preview = msg.type === 'VIDEO_MESSAGE' ? 'Видеосообщение'
                    : msg.type === 'AUDIO_MESSAGE' ? 'Аудиосообщение'
                    : msg.type === 'IMAGE' ? 'Изображение'
                    : msg.type === 'STICKER' ? 'Стикер'
                    : msg.type === 'CALL_HISTORY' ? 'Звонок'
                    : msg.attachments?.[0]?.fileName || msg.content || 'Новое сообщение';
                  showBrowserNotification(chatName, preview.substring(0, 100), msg.chatId);
                }
                get().incrementUnread(msg.chatId);
              }
            }
            break;
          }
          case 'message_edited': {
            const msg = data.data;
            const msgs = get().messages;
            const chatMsgs = (msgs[msg.chatId] || []).map((m) => (m.id === msg.id ? msg : m));
            set({ messages: { ...msgs, [msg.chatId]: chatMsgs } });
            break;
          }
          case 'message_deleted': {
            const { chatId, messageId } = data;
            const msgs = get().messages;
            const chatMsgs = (msgs[chatId] || []).map((m) =>
              m.id === messageId ? { ...m, isDeleted: true, content: '', attachments: [] } : m
            );            set({ messages: { ...msgs, [chatId]: chatMsgs } });
            break;
          }
          case 'message_pinned':
          case 'message_unpinned': {
            const msg = data.data;
            const msgs = get().messages;
            const chatMsgs = (msgs[msg.chatId] || []).map((m) => (m.id === msg.id ? msg : m));
            set({ messages: { ...msgs, [msg.chatId]: chatMsgs } });
            if (msg.chatId === get().activeChat) get().fetchPinnedMessages(msg.chatId);
            break;
          }
          case 'chat_updated': {
            const { chatId, name } = data;
            if (chatId && name != null) {
              set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, name } : c)) }));
            }
            break;
          }
          case 'chat_deleted': {
            const { chatId } = data;
            set((s) => {
              const fetchedChats = { ...s.fetchedChats };
              delete fetchedChats[chatId];
              return {
                chats: s.chats.filter((c) => c.id !== chatId),
                messages: { ...s.messages, [chatId]: [] },
                fetchedChats,
              };
            });
            if (get().activeChat === chatId) set({ activeChat: null });
            break;
          }
          case 'participant_removed': {
            const { chatId, userId } = data;
            if (userId === getCurrentUserId()) {
              set((s) => {
                const fetchedChats = { ...s.fetchedChats };
                delete fetchedChats[chatId];
                return {
                  chats: s.chats.filter((c) => c.id !== chatId),
                  messages: { ...s.messages, [chatId]: [] },
                  fetchedChats,
                };
              });
              if (get().activeChat === chatId) set({ activeChat: null });
            } else {
              set((s) => ({
                chats: s.chats.map((c) =>
                  c.id === chatId ? { ...c, participants: (c.participants || []).filter((p) => p.userId !== userId) } : c
                ),
              }));
            }
            break;
          }
          case 'typing': {
            const { chatId, userId } = data;
            set((s) => ({ typingUsers: { ...s.typingUsers, [chatId]: userId } }));
            if (typingTimers[chatId]) clearTimeout(typingTimers[chatId]);
            typingTimers[chatId] = setTimeout(() => {
              delete typingTimers[chatId];
              set((s) => { const t = { ...s.typingUsers }; delete t[chatId]; return { typingUsers: t }; });
            }, 5000);
            break;
          }
          case 'stop_typing': {
            const { chatId } = data;
            if (typingTimers[chatId]) { clearTimeout(typingTimers[chatId]); delete typingTimers[chatId]; }
            set((s) => { const t = { ...s.typingUsers }; delete t[chatId]; return { typingUsers: t }; });
            break;
          }
          case 'chat_request': {
            const req = data.data;
            if (req && req.id) {
              set((s) => (s.pendingRequests.some((r) => r.id === req.id)
                ? {}
                : { pendingRequests: [...s.pendingRequests, req] }));
            }
            break;
          }
          case 'chat_request_result': {
            const res = data.data;
            if (res && res.status === 'declined') {
              set({ requestResult: { status: 'declined' } });
            } else if (res && res.status === 'accepted' && res.chatId) {
              set((s) => ({ requestResult: { status: 'accepted', chatId: res.chatId } }));
              get().fetchChats().catch(() => {});
            }
            break;
          }
          case 'user_online': {
            const { userId } = data;
            set((s) => ({ onlineUsers: { ...s.onlineUsers, [userId]: true } }));
            break;
          }
          case 'user_offline': {
            const { userId } = data;
            set((s) => ({ onlineUsers: { ...s.onlineUsers, [userId]: false } }));
            break;
          }
          case 'presence': {
            const ids = new Set(data.userIds || []);
            set((s) => {
              const onlineUsers = { ...s.onlineUsers };
              ids.forEach((id) => { onlineUsers[id] = true; });
              Object.keys(onlineUsers).forEach((id) => {
                if (!ids.has(id)) onlineUsers[id] = false;
              });
              return { onlineUsers };
            });
            break;
          }
          case 'presence_offline': {
            const ids = data.userIds || [];
            set((s) => {
              const onlineUsers = { ...s.onlineUsers };
              ids.forEach((id) => { onlineUsers[id] = false; });
              return { onlineUsers };
            });
            break;
          }
          case 'ai_generation_completed': {
            window.dispatchEvent(new CustomEvent('ai_generation_done', {
              detail: { jobId: data.jobId, imageUrl: data.imageUrl, prompt: data.prompt },
            }));
            break;
          }
          case 'ai_generation_failed': {
            window.dispatchEvent(new CustomEvent('ai_generation_error', {
              detail: { jobId: data.jobId, prompt: data.prompt, messageId: data.messageId },
            }));
            break;
          }
          default:
            break;
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsUp(false);
      set({ isConnected: false });
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        const currentToken = localStorage.getItem('token');
        const cur = get().socket;
        if (currentToken && (!cur || cur.readyState === WebSocket.CLOSED)) {
          get().connectSocket(currentToken);
        }
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsUp(false);
    };

    set({ socket: ws });
  },

  disconnectSocket: () => {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    const ws = get().socket;
    if (ws) {
      ws.close();
      set({ socket: null, isConnected: false });
    }
  },

  fetchChats: async () => {
    try {
      const res = await api.get('/chats');
      let chats = res.data.content || res.data;
      if (!Array.isArray(chats)) chats = [];
      if (!chats.some((c) => c.name === 'Своё')) {
        try {
          const selfRes = await api.get('/chats/self');
          const selfChat = selfRes.data;
          if (selfChat && !chats.some((c) => c.id === selfChat.id)) {
            chats = [selfChat, ...chats];
          }
        } catch (e) {}
      }
      const unreadCounts = {};
      chats.forEach((c) => {
        if (c.unreadCount > 0) unreadCounts[c.id] = c.unreadCount;
      });
      set({ chats, unreadCounts, fetchError: '' });
    } catch (err) {
      set({ fetchError: err.response?.status === 401 ? 'Сессия истекла' : 'Не удалось загрузить чаты' });
      throw err;
    }
  },

  createChat: async (participantId) => {
    const res = await api.post('/chats', { participantIds: [participantId] });
    set((s) => ({ chats: [res.data, ...s.chats] }));
    return res.data;
  },

  createGroupChat: async (name, participantIds) => {
    const res = await api.post('/chats', { name, participantIds, isGroup: true });
    set((s) => ({ chats: [res.data, ...s.chats] }));
    return res.data;
  },

  setActiveChat: (chatId) => {
    set({ activeChat: chatId, unreadCounts: { ...get().unreadCounts, [chatId]: 0 } });
    get().markChatRead(chatId);
    const { fetchedChats, fetchingChats } = get();
    if (!fetchedChats[chatId] && !fetchingChats[chatId]) get().fetchMessages(chatId);
  },

  markChatRead: (chatId) => {
    api.put(`/chats/${chatId}/read`).catch(() => {});
  },

  toggleMute: async (chatId) => {
    const chat = get().chats.find((c) => c.id === chatId);
    const muted = !chat?.muted;
    set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, muted } : c)) }));
    try {
      await api.put(`/chats/${chatId}/mute`, { muted });
      return muted;
    } catch (e) {
      set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, muted: !muted } : c)) }));
      return !muted;
    }
  },

  renameChat: async (chatId, name) => {
    await api.put(`/chats/${chatId}/rename`, { name });
    set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, name } : c)) }));
  },

  fetchMessages: async (chatId) => {
    if (get().fetchingChats[chatId]) return;
    set((s) => ({ fetchingChats: { ...s.fetchingChats, [chatId]: true } }));
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await api.get(`/chats/${chatId}/messages`);
          const raw = res.data.content || res.data;
          const sorted = [...raw].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          set((s) => ({
            messages: { ...s.messages, [chatId]: sorted },
            fetchedChats: { ...s.fetchedChats, [chatId]: true },
          }));
          return;
        } catch (e) {
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 1500));
          } else {
            throw e;
          }
        }
      }
    } finally {
      set((s) => {
        const f = { ...s.fetchingChats };
        delete f[chatId];
        return { fetchingChats: f };
      });
    }
  },

  sendMessage: async (chatId, content, type = 'TEXT', replyToId) => {
    const res = await api.post(`/chats/${chatId}/messages`, { content, type, replyToId });
    const msgs = get().messages;
    const existing = msgs[chatId] || [];
    if (!existing.find((m) => m.id === res.data.id)) {
      const chatMsgs = [...existing, res.data].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      set({ messages: { ...msgs, [chatId]: chatMsgs } });
    }
    return res.data;
  },

  sendFileMessage: async (chatId, file, type = 'FILE', scheduledAt = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (scheduledAt) formData.append('scheduledAt', scheduledAt);
    const res = await api.post(`/chats/${chatId}/messages/file`, formData);
    if (scheduledAt) {
      get().fetchScheduledMessages();
      return res.data;
    }
    const msgs = get().messages;
    const existing = msgs[chatId] || [];
    if (!existing.find((m) => m.id === res.data.id)) {
      const chatMsgs = [...existing, res.data].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      set({ messages: { ...msgs, [chatId]: chatMsgs } });
    }
    return res.data;
  },

  forwardMessage: async (targetChatId, sourceChatId, messageId) => {
    const res = await api.post(`/chats/${targetChatId}/messages/forward`, { sourceChatId, messageId });
    const msgs = get().messages;
    const existing = msgs[targetChatId] || [];
    if (!existing.find((m) => m.id === res.data.id)) {
      const chatMsgs = [...existing, res.data].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      set({ messages: { ...msgs, [targetChatId]: chatMsgs } });
    }
    return res.data;
  },

  deleteChat: async (chatId) => {
    await api.delete(`/chats/${chatId}`);
    set((s) => {
      const fetchedChats = { ...s.fetchedChats };
      delete fetchedChats[chatId];
      return {
        chats: s.chats.filter((c) => c.id !== chatId),
        messages: { ...s.messages, [chatId]: [] },
        fetchedChats,
      };
    });
    if (get().activeChat === chatId) set({ activeChat: null });
  },

  unsubscribeChannel: async (chatId) => {
    await api.delete(`/chats/${chatId}/unsubscribe`);
    set((s) => {
      const fetchedChats = { ...s.fetchedChats };
      delete fetchedChats[chatId];
      return {
        chats: s.chats.filter((c) => c.id !== chatId),
        messages: { ...s.messages, [chatId]: [] },
        fetchedChats,
      };
    });
    if (get().activeChat === chatId) set({ activeChat: null });
  },

  removeChannelParticipant: async (chatId, targetUserId) => {
    await api.delete(`/chats/${chatId}/participants/${targetUserId}`);
    set((s) => ({
      chats: s.chats.map((c) =>
        c.id === chatId ? { ...c, participants: (c.participants || []).filter((p) => p.userId !== targetUserId) } : c
      ),
    }));
  },

  addReaction: async (chatId, messageId, emoji) => {
    const res = await api.post(`/chats/${chatId}/messages/${messageId}/reactions`, { emoji });
    const msgs = get().messages;
    const chatMsgs = (msgs[chatId] || []).map((m) => m.id === messageId ? { ...m, reactions: res.data } : m);
    set({ messages: { ...msgs, [chatId]: chatMsgs } });
  },

  removeReaction: async (chatId, messageId, emoji) => {
    const res = await api.delete(`/chats/${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
    const msgs = get().messages;
    const chatMsgs = (msgs[chatId] || []).map((m) => m.id === messageId ? { ...m, reactions: res.data } : m);
    set({ messages: { ...msgs, [chatId]: chatMsgs } });
  },

  editMessage: async (chatId, messageId, content) => {
    const res = await api.put(`/chats/${chatId}/messages/${messageId}`, { content });
    set((s) => {
      const msgs = { ...s.messages };
      msgs[chatId] = msgs[chatId].map((m) => m.id === messageId ? res.data : m);
      return { messages: msgs };
    });
  },

  deleteMessage: async (chatId, messageId) => {
    await api.delete(`/chats/${chatId}/messages/${messageId}`);
    set((s) => {
      const msgs = { ...s.messages };
      msgs[chatId] = msgs[chatId].map((m) =>
        m.id === messageId ? { ...m, isDeleted: true, content: '', attachments: [] } : m
      );
      return { messages: msgs };
    });
  },

  pinMessage: async (chatId, messageId) => {
    const res = await api.post(`/chats/${chatId}/messages/${messageId}/pin`);
    set((s) => {
      const msgs = { ...s.messages };
      msgs[chatId] = msgs[chatId].map((m) => (m.id === messageId ? res.data : m));
      return { messages: msgs };
    });
    if (chatId === get().activeChat) get().fetchPinnedMessages(chatId);
    return res.data;
  },

  unpinMessage: async (chatId, messageId) => {
    const res = await api.post(`/chats/${chatId}/messages/${messageId}/unpin`);
    set((s) => {
      const msgs = { ...s.messages };
      msgs[chatId] = msgs[chatId].map((m) => (m.id === messageId ? res.data : m));
      return { messages: msgs };
    });
    if (chatId === get().activeChat) get().fetchPinnedMessages(chatId);
    return res.data;
  },

  pinnedMessages: {},
  fetchPinnedMessages: async (chatId) => {
    try {
      const res = await api.get(`/chats/${chatId}/messages/pinned`);
      set((s) => ({ pinnedMessages: { ...s.pinnedMessages, [chatId]: res.data || [] } }));
    } catch (e) {}
  },

  scheduledMessages: [],
  fetchScheduledMessages: async () => {
    try {
      const res = await api.get('/scheduled-messages');
      set({ scheduledMessages: res.data || [] });
    } catch (e) {}
  },

  cancelScheduledMessage: async (messageId) => {
    await api.delete(`/scheduled-messages/${messageId}`);
    set((s) => ({ scheduledMessages: s.scheduledMessages.filter((m) => m.id !== messageId) }));
  },

  createInvite: async (email) => {
    const res = await api.post('/invites', { email });
    return res.data;
  },

  scheduleMessage: async (chatId, content, scheduledAt) => {
    const res = await api.post(`/chats/${chatId}/messages`, { content, type: 'TEXT', scheduledAt });
    get().fetchScheduledMessages();
    return res.data;
  },

  pendingRequests: [],
  requestResult: null,
  fetchChatRequests: async () => {
    try {
      const res = await api.get('/chat-requests/incoming');
      set({ pendingRequests: res.data || [] });
    } catch (e) {}
  },

  startDirectChat: async (targetUserId) => {
    set({ requestResult: null });
    const res = await api.post('/chat-requests/start', { targetUserId });
    const result = res.data;
    if (result.type === 'existing' || result.type === 'chat_created') {
      if (result.chat) {
        set((s) => (s.chats.some((c) => c.id === result.chat.id)
          ? { }
          : { chats: [result.chat, ...s.chats] }));
      }
    }
    return result;
  },

  acceptChatRequest: async (requestId) => {
    const res = await api.post(`/chat-requests/${requestId}/accept`);
    const chat = res.data;
    set((s) => ({
      pendingRequests: s.pendingRequests.filter((r) => r.id !== requestId),
      chats: s.chats.some((c) => c.id === chat.id) ? s.chats : [chat, ...s.chats],
    }));
    return chat;
  },

  declineChatRequest: async (requestId) => {
    await api.post(`/chat-requests/${requestId}/decline`);
    set((s) => ({ pendingRequests: s.pendingRequests.filter((r) => r.id !== requestId) }));
  },

  channels: [],
  channelsError: '',
  fetchChannels: async () => {
    try {
      const res = await api.get('/channels');
      set({ channels: res.data || [], channelsError: '' });
    } catch (err) {
      set({ channelsError: 'Не удалось загрузить каналы' });
      throw err;
    }
  },

  createChannel: async (name, visibility, allowComments, avatarUrl) => {
    const res = await api.post('/channels', { name, visibility, allowComments, avatarUrl });
    set((s) => ({ chats: [res.data, ...s.chats] }));
    return res.data;
  },

  uploadChannelAvatar: async (chatId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/chats/${chatId}/avatar`, formData);
    return res.data;
  },

  subscribeChannel: async (chatId) => {
    const res = await api.post(`/channels/${chatId}/subscribe`);
    set((s) => {
      const chats = [res.data, ...s.chats];
      return { chats, channels: s.channels.filter((c) => c.id !== chatId) };
    });
    return res.data;
  },

  emitTyping: (chatId) => {
    if (!chatId) return;
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'typing', chatId, typing: true }));
      clearTimeout(get()._typingTimeout);
      const timeout = setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'typing', chatId, typing: false }));
        }
      }, 3000);
      set({ _typingTimeout: timeout });
    }
  },

  incrementUnread: (chatId) => {
    if (chatId !== get().activeChat) {
      set((s) => ({ unreadCounts: { ...s.unreadCounts, [chatId]: (s.unreadCounts[chatId] || 0) + 1 } }));
    }
  },
}));
